import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Boxes,
  CheckCircle,
  Clock,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Package,
  RefreshCw,
  Settings,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import {
  ConnectionTestResult,
  DistributorConfig,
  DistributorProduct,
  ImportOptions,
  SeedSku,
  SyncResult,
  deleteDistributorProducts,
  getDistributorConfig,
  getDistributorProducts,
  getDistributorSeedSkus,
  importProductsToCatalogue,
  listEnrichments,
  saveDistributorConfig,
  saveDistributorSeedSkus,
  testDistributorConnection,
  triggerDistributorSync,
} from '../../services/distributorService';
import { DistributorShell, DistributorShellStatus } from '../DistributorsTab/DistributorShell';
import { AitryxDickerDataCobrand, DickerDataLockup, VendorMark } from '../IngramTab/lockups';
import { EnrichmentReviewPanel } from '../IngramTab/EnrichmentReviewPanel';
import { SyncHistoryPanel } from '../IngramTab/SyncHistoryPanel';
import { ProductsTable } from './ProductsTable';

type DickerTabKey = 'overview' | 'tracked' | 'review' | 'history' | 'config';

const DISTRIBUTOR_ID = 'dickerdata';

const tabs: Array<{ key: DickerTabKey; label: string; badge?: number }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'tracked', label: 'Live catalogue' },
  { key: 'review', label: 'Enrichment review' },
  { key: 'history', label: 'Sync history' },
  { key: 'config', label: 'Configuration' },
];

export function DickerDataTab() {
  const [config, setConfig] = useState<DistributorConfig | null>(null);
  const [products, setProducts] = useState<DistributorProduct[]>([]);
  const [seedSkus, setSeedSkus] = useState<SeedSku[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [apiToken, setApiToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [draftCount, setDraftCount] = useState<number | null>(null);
  const [tab, setTab] = useState<DickerTabKey>('overview');
  const [refreshKey, setRefreshKey] = useState(0);
  const [enrichingCount, setEnrichingCount] = useState(0);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [configData, productsData, skusData] = await Promise.all([
        getDistributorConfig(),
        getDistributorProducts().catch(() => ({ fetchedAt: null, products: [] })),
        getDistributorSeedSkus(DISTRIBUTOR_ID).catch(() => ({ createdAt: '', updatedAt: null, skus: [] })),
      ]);
      setConfig(configData);
      setProducts(productsData.products || []);
      setSeedSkus(skusData.skus || []);
      setTab((prev) => (configData.apiToken ? prev : 'config'));
    } catch (err: any) {
      setError(err.message || 'Failed to load Dicker Data configuration');
      setTab('config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!success) return;
    const timer = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(timer);
  }, [success]);

  const refreshDraftCount = useCallback(async () => {
    if (!config?.apiToken) return;
    try {
      const list = await listEnrichments(DISTRIBUTOR_ID, 'Draft');
      setDraftCount(list.items?.length ?? 0);
    } catch {
      setDraftCount(null);
    }
  }, [config?.apiToken]);

  useEffect(() => {
    refreshDraftCount();
  }, [refreshDraftCount, tab, syncing, refreshKey]);

  const enabledSkuCount = useMemo(
    () => seedSkus.filter((sku) => sku.enabled).length,
    [seedSkus],
  );

  const runEnrichmentDrainPoll = useCallback(
    async (baselineSkus: Set<string>, expected: number) => {
      const deadline = Date.now() + 90_000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 3_000));
        try {
          const list = await listEnrichments(DISTRIBUTOR_ID);
          const newRows = (list.items ?? []).filter((item) => !baselineSkus.has(item.sku));
          setRefreshKey((key) => key + 1);
          const remaining = Math.max(0, expected - newRows.length);
          setEnrichingCount(remaining);
          if (newRows.length >= expected) {
            setSuccess(
              expected === 1
                ? 'Enrichment complete: 1 new product is ready in Enrichment review.'
                : `Enrichment complete: ${expected} new products are ready in Enrichment review.`,
            );
            setEnrichingCount(0);
            return;
          }
        } catch {
          // Keep polling through transient marketplace/API failures.
        }
      }
      setEnrichingCount(0);
      setSuccess(
        'Sync completed. Enrichment is still running in the background — refresh in a minute or check Enrichment review.',
      );
    },
    [],
  );

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);
      setTestResult(null);
      setEnrichingCount(0);

      let baselineSkus = new Set<string>();
      try {
        const baseline = await listEnrichments(DISTRIBUTOR_ID);
        baselineSkus = new Set((baseline.items ?? []).map((item) => item.sku));
      } catch {
        // Non-fatal; the sync still runs and the UI refreshes after completion.
      }

      const result: SyncResult = await triggerDistributorSync(DISTRIBUTOR_ID);
      if (result.success) {
        const enqueued = result.enrichmentsEnqueued ?? 0;
        const baseMsg =
          `Sync completed: ${result.productsUpdated} products updated` +
          (result.productsFailed ? ` (${result.productsFailed} failed)` : '');
        if (enqueued > 0) {
          setSuccess(
            `${baseMsg}. Enriching ${enqueued} new ${enqueued === 1 ? 'product' : 'products'} in the background…`,
          );
          setEnrichingCount(enqueued);
          void runEnrichmentDrainPoll(baselineSkus, enqueued);
        } else {
          setSuccess(baseMsg);
        }
        setRefreshKey((key) => key + 1);
        await loadData();
      } else {
        setError(result.error || 'Sync failed');
      }
    } catch (err: any) {
      setError(err.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveSkus = async (updatedSkus: SeedSku[]) => {
    try {
      await saveDistributorSeedSkus(DISTRIBUTOR_ID, updatedSkus);
      setSeedSkus(updatedSkus);
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to save SKUs');
      return false;
    }
  };

  const handleImportProducts = async (
    productsToImport: DistributorProduct[],
    options: ImportOptions,
  ) => {
    try {
      const result = await importProductsToCatalogue(productsToImport, options);
      if (result.success) {
        const messages = [];
        if (result.importedCount > 0) messages.push(`${result.importedCount} new`);
        if (result.updatedCount > 0) messages.push(`${result.updatedCount} updated`);
        const countText = messages.length > 0 ? messages.join(', ') : 'No new';
        setSuccess(`Successfully imported ${countText} products to your catalogue`);
        return true;
      }
      setError(result.error || 'Import failed');
      return false;
    } catch (err: any) {
      setError(err.message || 'Failed to import products');
      return false;
    }
  };

  const handleDeleteSkusAndProducts = async (skusToDelete: string[]) => {
    try {
      const updatedSkus = seedSkus.filter((sku) => !skusToDelete.includes(sku.sku));
      await saveDistributorSeedSkus(DISTRIBUTOR_ID, updatedSkus);
      setSeedSkus(updatedSkus);

      const productsToDelete = skusToDelete.filter((sku) => products.some((product) => product.sku === sku));
      if (productsToDelete.length > 0) {
        await deleteDistributorProducts(productsToDelete);
      }

      setProducts(products.filter((product) => !skusToDelete.includes(product.sku)));
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to delete SKUs');
      return false;
    }
  };

  const handleTestConnection = async () => {
    if (!apiToken.trim()) {
      setError('Enter an API token to test');
      return;
    }
    try {
      setTesting(true);
      setError(null);
      setSuccess(null);
      setTestResult(null);
      const result = await testDistributorConnection(apiToken.trim());
      setTestResult(result);
    } catch (err: any) {
      setTestResult({
        success: false,
        error: err.message || 'Connection test failed',
        message: 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveApiToken = async () => {
    if (!apiToken.trim()) {
      setError('API token is required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      setTestResult(null);
      await saveDistributorConfig(apiToken.trim());
      setSuccess('Dicker Data API token saved.');
      setApiToken('');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to save API token');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-sm text-slate-500 inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading Dicker Data configuration…
      </div>
    );
  }

  const connected = !!config?.apiToken;
  const stale = isSyncStale(config);
  const shellStatus = getShellStatus(config, syncing);
  const shellTabs = tabs.map((item) => ({
    ...item,
    badge: item.key === 'review' ? draftCount ?? undefined : item.badge,
    disabled: !connected && item.key !== 'config',
  }));

  const actions = (
    <>
      {!syncing && enrichingCount > 0 && (
        <div className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-amber-50 border border-amber-200">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-700" />
          <span className="text-[12.5px] text-amber-900">
            Enriching <strong className="font-semibold tabular-nums">{enrichingCount}</strong>
          </span>
        </div>
      )}
      <Button
        variant="outline"
        onClick={() => setTab('config')}
        disabled={saving || testing || syncing}
        className="inline-flex h-9 gap-1.5"
      >
        <Settings className="h-3.5 w-3.5" />
        Configure
      </Button>
      <Button
        onClick={handleSync}
        disabled={!connected || saving || testing || syncing || enabledSkuCount === 0}
        className="inline-flex h-9 gap-1.5 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-600 text-white"
        title={
          !connected
            ? 'Configure the connection first'
            : enabledSkuCount === 0
              ? 'Add tracked SKUs before syncing'
              : 'Trigger a manual Dicker Data sync now'
        }
      >
        {syncing ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Syncing…
          </>
        ) : (
          <>
            <RefreshCw className="h-3.5 w-3.5" />
            Sync now
          </>
        )}
      </Button>
    </>
  );

  const banners = (error || success || testResult || stale) ? (
    <>
      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {success}
        </div>
      )}
      {testResult && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${
            testResult.success
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-rose-200 bg-rose-50 text-rose-700'
          }`}
        >
          <div className="font-medium">
            {testResult.success ? 'Connection successful' : 'Connection failed'}
          </div>
          <div className="text-xs opacity-80">
            {testResult.error || testResult.message}
          </div>
        </div>
      )}
      {stale && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Pricing data may be stale. Last sync was {staleSinceHours(config)} hours ago.
        </div>
      )}
    </>
  ) : null;

  return (
    <DistributorShell<DickerTabKey>
      lockup={<AitryxDickerDataCobrand />}
      title="Dicker Data Connector"
      currentCrumb="Dicker Data"
      status={shellStatus}
      actions={actions}
      tabs={shellTabs}
      activeTab={tab}
      onTabChange={setTab}
      banners={banners}
    >
      {tab === 'overview' && connected && (
        <DickerOverviewSection
          config={config}
          products={products}
          seedSkus={seedSkus}
          syncing={syncing}
          onSync={handleSync}
          onOpenTracked={() => setTab('tracked')}
          onOpenConfig={() => setTab('config')}
        />
      )}
      {tab === 'tracked' && connected && (
        <div className="px-6 py-6">
          <ProductsTable
            seedSkus={seedSkus}
            products={products}
            onSaveSkus={handleSaveSkus}
            onDeleteSkusAndProducts={handleDeleteSkusAndProducts}
            onImportProducts={handleImportProducts}
            onError={setError}
            onSuccess={setSuccess}
          />
        </div>
      )}
      {tab === 'review' && connected && (
        <EnrichmentReviewPanel
          distributorId={DISTRIBUTOR_ID}
          distributorLabel="Dicker Data"
          refreshKey={refreshKey}
        />
      )}
      {tab === 'history' && connected && (
        <SyncHistoryPanel distributorId={DISTRIBUTOR_ID} distributorLabel="Dicker Data" />
      )}
      {tab === 'config' && (
        <DickerConfigurationSection
          config={config}
          apiToken={apiToken}
          setApiToken={setApiToken}
          showToken={showToken}
          setShowToken={setShowToken}
          saving={saving}
          testing={testing}
          syncing={syncing}
          onTest={handleTestConnection}
          onSave={handleSaveApiToken}
        />
      )}
    </DistributorShell>
  );
}

interface DickerOverviewSectionProps {
  config: DistributorConfig | null;
  products: DistributorProduct[];
  seedSkus: SeedSku[];
  syncing: boolean;
  onSync: () => void;
  onOpenTracked: () => void;
  onOpenConfig: () => void;
}

function DickerOverviewSection({
  config,
  products,
  seedSkus,
  syncing,
  onSync,
  onOpenTracked,
  onOpenConfig,
}: DickerOverviewSectionProps) {
  const enabledSkus = seedSkus.filter((sku) => sku.enabled);
  const stale = isSyncStale(config);
  const syncedCount = config?.productCount ?? products.length ?? 0;
  const syncStatus = config?.lastSyncStatus || 'Ready';

  return (
    <div className="px-6 py-6 space-y-6 animate-[fadeIn_.3s_ease-out]">
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12">
          <div className="col-span-12 lg:col-span-8 p-7">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-slate-500 font-medium mb-3">
              <span className="inline-flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${stale ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                {stale ? 'Pricing needs refresh' : 'Connection live'}
                {config?.lastSyncAt ? ` · synced ${formatRelative(config.lastSyncAt)}` : ''}
              </span>
            </div>

            <h1 className="text-[32px] leading-[1.1] font-semibold text-slate-900 tracking-tight">
              Dicker Data catalogue,{' '}
              <span className="text-slate-500 font-normal">tracked by SKU.</span>
            </h1>
            <p className="text-slate-600 mt-2 text-[14px] leading-relaxed max-w-xl">
              Keep your curated Dicker Data list refreshed for buy price, stock, and customer catalogue updates.
            </p>

            <div className="mt-6 grid grid-cols-3 gap-0 max-w-2xl border border-slate-200 rounded-lg overflow-hidden bg-slate-50/30">
              <HeroMetric
                label="Tracked SKUs"
                value={seedSkus.length}
                sub={`${enabledSkus.length} enabled for sync`}
              />
              <HeroMetric
                label="Products synced"
                value={syncedCount}
                sub={config?.lastSyncAt ? `last run ${formatRelative(config.lastSyncAt)}` : 'sync to populate'}
                tone={syncedCount > 0 ? 'neutral' : 'orange'}
                divider
              />
              <HeroMetric
                label="Sync status"
                value={stale ? 'Stale' : syncStatus}
                sub={stale ? `${staleSinceHours(config)}h since last sync` : 'ready for next run'}
                tone={stale || config?.lastSyncStatus === 'error' ? 'orange' : 'neutral'}
                divider
              />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 bg-slate-50/60 border-l border-slate-200 p-7 flex flex-col justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-medium mb-3">
                Distributor connection
              </div>
              <div className="flex items-center gap-2 mb-4">
                <DickerDataLockup />
                <span className="inline-flex items-center gap-1.5 rounded px-2 h-5 text-[11px] font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Connected
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <MetricPair label="Sync rate" value="Daily + manual" />
                <MetricPair label="Last run" value={config?.lastSyncAt ? formatRelative(config.lastSyncAt) : 'Never'} />
                <MetricPair label="Cache size" value={`${syncedCount} SKUs`} />
                <MetricPair label="Enabled" value={`${enabledSkus.length} SKUs`} />
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                onClick={onSync}
                disabled={syncing || enabledSkus.length === 0}
                className="inline-flex h-9 gap-1.5"
              >
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Sync now
              </Button>
              <Button variant="outline" onClick={onOpenTracked} className="inline-flex h-9 gap-1.5">
                <Boxes className="h-3.5 w-3.5" />
                Manage SKUs
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-baseline justify-between">
              <div>
                <h3 className="text-[14px] font-semibold text-slate-900 flex items-center gap-2">
                  <Package className="h-4 w-4 text-slate-700" />
                  SKU watchlist · {seedSkus.length} item{seedSkus.length === 1 ? '' : 's'}
                </h3>
                <p className="text-[12px] text-slate-500 mt-0.5">
                  The Dicker Data products you actively resell and keep refreshed.
                </p>
              </div>
            </div>
            {seedSkus.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <p className="text-[13px] text-slate-700 font-medium">No tracked SKUs yet</p>
                <p className="text-[12px] text-slate-500 mt-1">
                  Add SKUs in Live catalogue, then run a sync to pull price and stock.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {seedSkus.slice(0, 6).map((sku) => {
                  const product = products.find((item) => item.sku === sku.sku);
                  return (
                    <div key={sku.sku} className="px-6 py-3 flex items-center gap-3">
                      <VendorMark vendor={product?.brand ?? sku.name} size={24} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[12px] text-orange-700">{sku.sku}</span>
                          {!sku.enabled && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                              Paused
                            </span>
                          )}
                        </div>
                        <div className="text-[12.5px] text-slate-700 truncate">
                          {product?.description ?? sku.name ?? 'Awaiting sync'}
                        </div>
                      </div>
                      <div className="text-right text-[12px] text-slate-500 tabular-nums">
                        {product?.unitPrice != null ? formatMoney(product.unitPrice) : '—'}
                        <div className="text-[10.5px] text-slate-400">
                          SOH {product?.soh ?? '—'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden h-full">
            <div className="px-5 pt-5 pb-4 border-b border-slate-100">
              <h3 className="text-[14px] font-semibold text-slate-900 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-orange-500" />
                Operational checks
              </h3>
              <p className="text-[12px] text-slate-500 mt-1">
                What to look at before publishing Dicker Data changes.
              </p>
            </div>
            <div className="p-5 space-y-3">
              <ChecklistItem
                Icon={KeyRound}
                title="Token configured"
                detail="Rotate the token from Configuration when Dicker Data issues a new key."
                ok={!!config?.apiToken}
                onClick={onOpenConfig}
              />
              <ChecklistItem
                Icon={Clock}
                title="Sync freshness"
                detail={config?.lastSyncAt ? `Last sync ${formatRelative(config.lastSyncAt)}` : 'No sync has completed yet.'}
                ok={!stale && !!config?.lastSyncAt}
              />
              <ChecklistItem
                Icon={Sparkles}
                title="AI review"
                detail="Newly synced SKUs can be approved in Enrichment review before publishing."
                ok={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface DickerConfigurationSectionProps {
  config: DistributorConfig | null;
  apiToken: string;
  setApiToken: (value: string) => void;
  showToken: boolean;
  setShowToken: (value: boolean) => void;
  saving: boolean;
  testing: boolean;
  syncing: boolean;
  onTest: () => void;
  onSave: () => void;
}

function DickerConfigurationSection({
  config,
  apiToken,
  setApiToken,
  showToken,
  setShowToken,
  saving,
  testing,
  syncing,
  onTest,
  onSave,
}: DickerConfigurationSectionProps) {
  return (
    <div className="px-6 pb-10 max-w-3xl">
      <div className="mt-4 rounded-lg border border-slate-200 bg-white p-6 space-y-5">
        <div>
          <h2 className="text-[18px] font-semibold text-slate-900 tracking-tight">
            Dicker Data API token
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Store the partner B2B API token used to refresh pricing and stock. Existing tokens are never shown.
          </p>
        </div>

        {config?.apiToken && (
          <div className="grid grid-cols-3 gap-3">
            <ConfigStat label="Last sync" value={formatDate(config.lastSyncAt)} />
            <ConfigStat label="Products synced" value={String(config.productCount ?? 0)} />
            <ConfigStat label="Sync status" value={config.lastSyncStatus || 'Ready'} />
          </div>
        )}

        <div>
          <label htmlFor="dicker-api-token" className="block text-xs font-medium text-slate-700 mb-1">
            API token
          </label>
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[260px]">
              <input
                id="dicker-api-token"
                type={showToken ? 'text' : 'password'}
                value={apiToken}
                onChange={(event) => setApiToken(event.target.value)}
                placeholder={config?.apiToken ? '••••••••••••••••' : 'Enter your Dicker Data API token'}
                className="w-full rounded-md border border-slate-300 px-3 py-2 pr-10 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoComplete="off"
                spellCheck={false}
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                title={showToken ? 'Hide token' : 'Show token'}
                aria-label={showToken ? 'Hide token' : 'Show token'}
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={onTest}
              disabled={testing || saving || syncing || !apiToken.trim()}
              loading={testing}
              className="inline-flex"
            >
              {testing ? 'Testing…' : 'Test connection'}
            </Button>
            <Button
              type="button"
              onClick={onSave}
              disabled={saving || testing || syncing || !apiToken.trim()}
              loading={saving}
              className="inline-flex"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
          {config?.apiToken && (
            <p className="mt-2 text-[11px] text-slate-500">
              A token is already configured. Enter a new one to rotate it.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function getShellStatus(config: DistributorConfig | null, syncing: boolean): DistributorShellStatus {
  if (syncing) {
    return {
      label: 'Syncing',
      tone: 'syncing',
      description: 'Refreshing Dicker Data pricing and stock',
    };
  }
  if (!config?.apiToken) {
    return {
      label: 'Not connected',
      tone: 'neutral',
      description: 'Configure your Dicker Data API token to start syncing',
    };
  }
  if (config.lastSyncStatus === 'error') {
    return {
      label: 'Error',
      tone: 'error',
      description: config.lastSyncError || 'Last sync failed',
    };
  }
  if (isSyncStale(config)) {
    return {
      label: 'Stale',
      tone: 'warning',
      description: 'Pricing data needs a fresh sync',
    };
  }
  return {
    label: 'Connected',
    tone: 'connected',
    description: 'B2B pricing and stock from a curated SKU list',
  };
}

function isSyncStale(config: DistributorConfig | null): boolean {
  if (!config?.lastSyncAt) return false;
  const lastSync = new Date(config.lastSyncAt).getTime();
  const hoursAgo = (Date.now() - lastSync) / (1000 * 60 * 60);
  return hoursAgo > 36;
}

function staleSinceHours(config: DistributorConfig | null): number {
  if (!config?.lastSyncAt) return 0;
  const lastSync = new Date(config.lastSyncAt).getTime();
  return Math.round((Date.now() - lastSync) / (1000 * 60 * 60));
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString();
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function formatMoney(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function MetricPair({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-slate-500">{label}</div>
      <div className="text-slate-900 font-medium tabular-nums">{value}</div>
    </div>
  );
}

function HeroMetric({
  label,
  value,
  sub,
  tone = 'neutral',
  divider,
}: {
  label: string;
  value: number | string;
  sub: string;
  tone?: 'neutral' | 'orange';
  divider?: boolean;
}) {
  const toneClass = tone === 'orange' ? 'text-orange-600' : 'text-slate-900';
  return (
    <div className={`p-5 ${divider ? 'border-l border-slate-200' : ''}`}>
      <div className={`text-[34px] font-semibold tabular-nums tracking-tight leading-none ${toneClass}`}>
        {value}
      </div>
      <div className="text-[12.5px] text-slate-800 font-medium mt-2">{label}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function ChecklistItem({
  Icon,
  title,
  detail,
  ok,
  onClick,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  title: string;
  detail: string;
  ok: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      <div className={`h-8 w-8 rounded-lg grid place-items-center ${ok ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <div className="text-sm font-medium text-slate-900">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{detail}</div>
      </div>
      {ok ? (
        <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-full flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:bg-slate-50 transition-colors"
      >
        {content}
      </button>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
      {content}
    </div>
  );
}

function ConfigStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-900 truncate">{value}</div>
    </div>
  );
}

export default DickerDataTab;
